import React, { useState, useEffect, useRef } from 'react';
import TableHead from '../ui/TableHead';
import TableRow from '../ui/TableRow';
import { ChevronDown } from 'lucide-react';

function TableNew({
  isFor,
  name,
  headers,
  data,
  handleRowClick,
  isDownloadable,
  sortOptions,
  isFilterable,
  onYes,
  onNo,
  identifier,
  isDeleteEnable,
  onApprove,
  isViewEnable,
  isImportEnable,
  onImport,
  isEditEnable,
  onEdit,
  onDelete,
  onView,
  onChangeStatus,
  isChangeStatusEnable,
  bigHeaders,
  onAccept,
  isAcceptEnable,
  onCancel,
  isCancelEnable,
  onViewBrands,
  onViewItems,
  isStatusUpdateEnable,
  onStatusUpdate,
  isMarkEnable,
  onMark,
  isDollerEnable,
  onDollor,
  onViewByName,
  isViewByName,
  rowName,
  isBtnEnable,
  onBtn,
  onViewNote,
  isDownloadInvoiceEnable,
  onDownloadInvoice,
  isDownloadBillEnable,
  onDownloadBill,
  onResendInvite,
  isInventoryActionEnabled,
  onInventoryPurchase,
  onInventoryUpdate,
  onDownloadDoc,
  isDownloadDocEnable,
  onUploadDoc,
  uploadDocId,
  onDownloadSignDoc,
  onRowClick,
  // New props for the enhanced functionality
  stickyOffset = 0, // At what point headers become sticky (in px)
  rowHeight = 60, // Estimated height per row in px
  headerHeight = 48, // Height of header in px
  maxHeight = null, // Maximum table height (if null, uses screen height)
  className = '',
  style = {}
}) {
  /* 
    Local state
  */
  const [query, setQuery] = useState('');
  const [openDropdown, setOpenDropdown] = useState(null);
  const [tableHeight, setTableHeight] = useState(null);
  const tableRef = useRef(null);

  /* 
    Computation
  */
  const width = 100 / headers.length >= 25 ? 100 / headers.length - 1 : 100 / headers.length;

  // Calculate dynamic height based on data
  useEffect(() => {
    if (data && data.length > 0) {
      const contentHeight = data.length * rowHeight + headerHeight;
      const maxScreenHeight = maxHeight || window.innerHeight - stickyOffset - 100; // Reserve some space
      const calculatedHeight = Math.min(contentHeight, maxScreenHeight);
      setTableHeight(calculatedHeight);
    } else {
      // No data, just show header
      setTableHeight(headerHeight + 100); // Header + some space for "no data" message
    }
  }, [data, rowHeight, headerHeight, stickyOffset, maxHeight]);

  function handleQueryChange(e) {
    const targetValue = e.target.value;
    setQuery(targetValue);
  }

  // Helper to get header label
  const getHeaderLabel = (header) => (typeof header === 'string' ? header : header.label);

  // Determine if content needs scrolling
  const needsScrolling = data && data.length * rowHeight > tableHeight - headerHeight;

  return (
    <div
      ref={tableRef}
      className={`w-full bg-white rounded-xl relative ${className}`}
      style={{
        height: tableHeight ? `${tableHeight}px` : 'auto',
        maxHeight: maxHeight ? `${maxHeight}px` : `calc(100vh - ${stickyOffset}px)`,
        overflow: 'hidden', // Ensure no content flows outside
        ...style
      }}>
      <div role="table" className="h-full flex flex-col" style={{ position: 'relative' }}>
        {/* Sticky Header */}
        <div
          className="w-full justify-between p-3 flex !text-gray-600"
          style={{
            position: 'sticky',
            top: `${stickyOffset}px`,
            left: 0,
            right: 0,
            zIndex: 20, // Higher than Headroom (z-10) to appear above it
            backgroundColor: '#e5e7eb', // Solid background to hide content flowing behind
            borderBottom: '1px solid #d1d5db', // Optional: adds separation line
            margin: '0',
            width: '100%',
            borderTopLeftRadius: '0.75rem',
            borderTopRightRadius: '0.75rem',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)' // Shadow when sticky
          }}>
          {headers?.map((header, idx) => {
            const isDropdown = typeof header === 'object' && header.dropdown;
            return (
              <div
                key={getHeaderLabel(header)}
                className="relative flex items-center justify-start gap-x-10"
                style={{ width: `${width}%` }}>
                <TableHead name={getHeaderLabel(header)} width={width} />
                {isDropdown && (
                  <>
                    <ChevronDown
                      className="ml-1 cursor-pointer"
                      size={16}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOpenDropdown(openDropdown === idx ? null : idx);
                      }}
                    />
                    {openDropdown === idx && (
                      <div
                        className="absolute left-0 top-full mt-2 bg-white border rounded shadow z-50 min-w-[10rem] max-h-[250px] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}>
                        {header.dropdownOptions?.map((opt) => (
                          <div
                            key={opt.id}
                            className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                            onClick={() => {
                              setOpenDropdown(null);
                              header.onDropdownSelect && header.onDropdownSelect(opt.id);
                            }}>
                            {opt.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Table Body */}
        <div
          className={`flex-1 bg-white w-full table-body rounded-b-xl ${bigHeaders && 'mt-[2rem]'}`}
          style={{
            overflowY: needsScrolling ? 'auto' : 'visible',
            overflowX: 'hidden',
            marginTop: '0', // Ensure no gap between header and body
            paddingTop: '0' // Ensure no padding that creates white space
          }}>
          {data && data.length > 0 ? (
            data?.map((item, index) => (
              <div key={`${JSON.stringify(item)}-${index}`} style={{ minHeight: `${rowHeight}px` }}>
                <TableRow
                  data={item}
                  width={width}
                  identifier={identifier}
                  handleRowClick={onRowClick || handleRowClick}
                  isDeleteEnable={isDeleteEnable}
                  isViewEnable={isViewEnable}
                  isImportEnable={isImportEnable}
                  onImport={onImport}
                  isEditEnable={isEditEnable}
                  onMark={onMark}
                  onYes={onYes}
                  onNo={onNo}
                  onApprove={onApprove}
                  onView={onView}
                  onEdit={onEdit}
                  onDelete={onDelete}
                  isAcceptEnable={isAcceptEnable}
                  onAccept={onAccept}
                  onChangeStatus={onChangeStatus}
                  isChangeStatusEnable={isChangeStatusEnable}
                  isCancelEnable={isCancelEnable}
                  onCancel={onCancel}
                  onViewBrands={onViewBrands}
                  onViewItems={onViewItems}
                  isStatusUpdateEnable={isStatusUpdateEnable}
                  onStatusUpdate={onStatusUpdate}
                  isMarkEnable={isMarkEnable}
                  isDollerEnable={isDollerEnable}
                  onDollor={onDollor}
                  onViewByName={onViewByName}
                  isViewByName={isViewByName}
                  rowName={rowName}
                  isBtnEnable={isBtnEnable}
                  onBtn={onBtn}
                  onViewNote={onViewNote}
                  isDownloadInvoiceEnable={isDownloadInvoiceEnable}
                  onDownloadInvoice={onDownloadInvoice}
                  isDownloadBillEnable={isDownloadBillEnable}
                  onDownloadBill={onDownloadBill}
                  onResendInvite={onResendInvite}
                  isInventoryActionEnabled={isInventoryActionEnabled}
                  onInventoryPurchase={onInventoryPurchase}
                  onInventoryUpdate={onInventoryUpdate}
                  onDownloadDoc={onDownloadDoc}
                  isDownloadDocEnable={isDownloadDocEnable}
                  onUploadDoc={onUploadDoc}
                  uploadDocId={uploadDocId}
                  onDownloadSignDoc={onDownloadSignDoc}
                  className="hover:bg-gray-50 transition-all duration-200 cursor-pointer"
                />
              </div>
            ))
          ) : (
            <div className="flex justify-center items-center h-40">
              <p className="text-gray-400">No data found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TableNew;
